import {MouseEvent, ReactNode, TouchEvent, useCallback, useRef, useState} from 'react';
import {Box} from '@mui/material';

type PressableConfigurations = {
  shouldPreventDefault: boolean;
  delay: number;
};

export type InteractionEvent = MouseEvent<HTMLDivElement> | TouchEvent<HTMLDivElement>;

interface PressableProps {
  children: ReactNode;
  onLongPress: (event: InteractionEvent) => void;
  onClick?: (event: InteractionEvent) => void;
  configurations?: PressableConfigurations;
}

const PRESSABLE_DEFAULTS: PressableConfigurations = {shouldPreventDefault: true, delay: 1000};

export const Pressable = ({children, onLongPress, onClick, configurations}: PressableProps) => {
  const [longPressTriggered, setLongPressTriggered] = useState(false);
  const timeout = useRef<ReturnType<typeof setTimeout>>();
  const target = useRef<EventTarget>();

  const {shouldPreventDefault, delay} = {...PRESSABLE_DEFAULTS, ...configurations};

  //@ts-ignore
  const isTouchEvent = (event: Event): event is TouchEvent => 'touches' in event;

  const preventDefault = useCallback((event: Event) => {
    if (!isTouchEvent(event)) return;

    if (event.touches.length < 2 && event.preventDefault) {
      event.preventDefault();
    }
  }, []);

  const start = useCallback(
    (event: InteractionEvent) => {
      if (shouldPreventDefault && event?.target) {
        event.target.addEventListener('touchend', preventDefault, {
          passive: false,
        });

        target.current = event.target;
      }

      timeout.current = setTimeout(() => {
        onLongPress(event);
        setLongPressTriggered(true);
      }, delay);
    },
    [shouldPreventDefault, delay, preventDefault, onLongPress]
  );

  const clear = useCallback(
    (event: InteractionEvent, shouldTriggerClick = true) => {
      timeout.current && clearTimeout(timeout.current);
      shouldTriggerClick && !longPressTriggered && onClick && onClick(event);
      setLongPressTriggered(false);
      if (shouldPreventDefault && target.current) {
        target?.current?.removeEventListener('touchend', preventDefault);
      }
    },
    [longPressTriggered, onClick, shouldPreventDefault, preventDefault]
  );

  return (
    <Box
      onMouseDown={start}
      onMouseUp={clear}
      onMouseLeave={(e: InteractionEvent) => clear(e, false)}
      onTouchStart={start}
      onTouchEnd={clear}
      sx={{width: '100%'}}
    >
      {children}
    </Box>
  );
};
